export function formatPriceMinor(amountMinor: number, currency = 'GBP', locale = 'en-GB'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amountMinor / 100);
}

export function formatDateInTz(iso: string, timezone: string, locale = 'en-GB'): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: timezone }).format(
    new Date(iso),
  );
}

export function formatDateTimeInTz(iso: string, timezone: string, locale = 'en-GB'): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: timezone,
  }).format(new Date(iso));
}

// ---------------------------------------------------------------------------
// i18n-friendly helpers (sub-spec i18n scaffolding).
//
// These deliberately take the locale as a required parameter rather than
// reading from a global — that way the customer portal can call them with
// `useLocale().locale` and the staff portal can pass its own without
// either side leaking into the other. Currency stays an explicit param;
// the currency belongs to the tenant ("we charge in GBP"), the locale
// belongs to the customer ("I read prices in fr-CA format"), so a
// French-Canadian customer of a UK tenant sees `19,99 £` not `£19.99`.
// ---------------------------------------------------------------------------

/**
 * Format a money amount in minor units (pence / cents) into a localised
 * currency string. Currency stays in the tenant's currency; the locale
 * controls the formatting (separator, symbol placement, etc.).
 *
 *   formatMoney(1995, 'GBP', 'en-GB')  → '£19.95'
 *   formatMoney(1995, 'GBP', 'fr-CA')  → '19,95 £'
 *   formatMoney(1995, 'USD', 'es-US')  → 'US$19.95'
 *   formatMoney(1995, 'EUR', 'de-DE')  → '19,95 €'
 */
export function formatMoney(amountMinor: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amountMinor / 100);
}

export interface FormatDateOpts {
  dateStyle?: 'full' | 'long' | 'medium' | 'short';
  timeStyle?: 'full' | 'long' | 'medium' | 'short';
  timeZone?: string;
}

/**
 * Format an ISO 8601 timestamp string into a locale-aware date string.
 * Defaults to `dateStyle: 'medium'` if no styles are given.
 *
 *   formatDate('2026-05-23T09:00:00Z', 'en-GB')
 *     → '23 May 2026'
 *   formatDate('2026-05-23T09:00:00Z', 'fr-CA', { dateStyle: 'long' })
 *     → '23 mai 2026'
 */
export function formatDate(iso: string, locale: string, opts: FormatDateOpts = {}): string {
  const intlOpts: Intl.DateTimeFormatOptions = {};
  if (opts.dateStyle) intlOpts.dateStyle = opts.dateStyle;
  if (opts.timeStyle) intlOpts.timeStyle = opts.timeStyle;
  if (opts.timeZone) intlOpts.timeZone = opts.timeZone;
  if (!opts.dateStyle && !opts.timeStyle) intlOpts.dateStyle = 'medium';
  return new Intl.DateTimeFormat(locale, intlOpts).format(new Date(iso));
}
